/**
 * SSOT: Datos Fiscales, Bancarios y de Autorización por País
 *
 * La DB guarda valores genéricos en config_json:
 *   tipo_documento_fiscal, numero_documento_fiscal, razon_social,
 *   regimen_fiscal, banco, tipo_cuenta_bancaria, numero_cuenta,
 *   cuenta_interbancaria, autorizacion_codigo, autorizacion_vencimiento, etc.
 *
 * Este archivo define CÓMO se llaman esos campos y QUÉ opciones hay en cada país.
 * La UI consulta estos presets para mostrar labels, placeholders y opciones correctas.
 *
 * Triple compatibilidad: ESM (fronts), CJS (backend), IIFE (admin legacy)
 */

const DATOS_FISCALES_POR_PAIS = {
    PE: {
        nombre: 'Perú',
        moneda: 'PEN',
        prefijo: '+51',
        telefono: {
            regex: /^9[0-9]{8}$/,
            digitos: 9,
            placeholder: '999 888 777',
            ejemplo: '999888777',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'RUC',
                nombre: 'RUC (Registro Único de Contribuyentes)',
                digitos: 11,
                placeholder: '20612515833',
            },
            { codigo: 'DNI', nombre: 'DNI (Documento Nacional de Identidad)', digitos: 8, placeholder: '12345678' },
            { codigo: 'CE', nombre: 'Carné de Extranjería', digitos: null, placeholder: '' },
        ],
        regimenes: [
            { codigo: 'MYPE', nombre: 'Régimen MYPE Tributario' },
            { codigo: 'RUS', nombre: 'Régimen Único Simplificado (RUS)' },
            { codigo: 'RER', nombre: 'Régimen Especial de Renta (RER)' },
            { codigo: 'RG', nombre: 'Régimen General' },
        ],
        bancos: [
            'BCP (Banco de Crédito del Perú)',
            'Interbank',
            'BBVA Perú',
            'Scotiabank Perú',
            'BanBif',
            'Banco Pichincha',
            'Banco de la Nación',
            'MiBanco',
            'Banco Falabella',
            'Banco Ripley',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorros', nombre: 'Cuenta de Ahorros' },
            { codigo: 'maestra', nombre: 'Cuenta Maestra' },
            { codigo: 'cts', nombre: 'CTS' },
        ],
        cuenta_interbancaria: {
            nombre: 'CCI (Código de Cuenta Interbancaria)',
            digitos: 20,
            placeholder: '00223200421497200772',
        },
        autorizacion: {
            nombre: 'Licencia Municipal de Funcionamiento',
            tiene_vencimiento: true,
        },
    },

    BR: {
        nombre: 'Brasil',
        moneda: 'BRL',
        prefijo: '+55',
        telefono: {
            regex: /^[1-9]{2}9[0-9]{8}$/,
            digitos: 11,
            placeholder: '11 99888 7766',
            ejemplo: '11999887766',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'CNPJ',
                nombre: 'CNPJ (Cadastro Nacional da Pessoa Jurídica)',
                digitos: 14,
                placeholder: '12345678000199',
            },
            { codigo: 'CPF', nombre: 'CPF (Cadastro de Pessoas Físicas)', digitos: 11, placeholder: '12345678901' },
        ],
        regimenes: [
            { codigo: 'SN', nombre: 'Simples Nacional' },
            { codigo: 'LP', nombre: 'Lucro Presumido' },
            { codigo: 'LR', nombre: 'Lucro Real' },
            { codigo: 'MEI', nombre: 'Microempreendedor Individual (MEI)' },
        ],
        bancos: [
            'Banco do Brasil',
            'Itaú Unibanco',
            'Bradesco',
            'Santander Brasil',
            'Caixa Econômica Federal',
            'Nubank',
            'Banco Inter',
            'BTG Pactual',
            'Sicoob',
            'Banco Safra',
        ],
        tipos_cuenta: [
            { codigo: 'corrente', nombre: 'Conta Corrente' },
            { codigo: 'poupanca', nombre: 'Conta Poupança' },
            { codigo: 'pagamento', nombre: 'Conta de Pagamento' },
        ],
        cuenta_interbancaria: {
            nombre: 'Agência / Conta',
            digitos: null,
            placeholder: '0001 / 12345-6',
        },
        autorizacion: {
            nombre: 'Alvará de Funcionamento',
            tiene_vencimiento: true,
        },
    },

    CO: {
        nombre: 'Colombia',
        moneda: 'COP',
        prefijo: '+57',
        telefono: {
            regex: /^3[0-9]{9}$/,
            digitos: 10,
            placeholder: '300 123 4567',
            ejemplo: '3001234567',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'NIT',
                nombre: 'NIT (Número de Identificación Tributaria)',
                digitos: 10,
                placeholder: '9001234567',
            },
            { codigo: 'CC', nombre: 'Cédula de Ciudadanía', digitos: 10, placeholder: '1234567890' },
            { codigo: 'CE', nombre: 'Cédula de Extranjería', digitos: null, placeholder: '' },
        ],
        regimenes: [
            { codigo: 'RS', nombre: 'Régimen Simple de Tributación' },
            { codigo: 'RO', nombre: 'Régimen Ordinario' },
            { codigo: 'RNA', nombre: 'Responsable de IVA' },
            { codigo: 'NR', nombre: 'No Responsable de IVA' },
        ],
        bancos: [
            'Bancolombia',
            'Banco de Bogotá',
            'Davivienda',
            'Banco de Occidente',
            'BBVA Colombia',
            'Banco Popular',
            'Banco AV Villas',
            'Banco Caja Social',
            'Nequi',
            'Daviplata',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorros', nombre: 'Cuenta de Ahorros' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Concepto de Uso de Suelo / Licencia Comercial',
            tiene_vencimiento: true,
        },
    },

    CL: {
        nombre: 'Chile',
        moneda: 'CLP',
        prefijo: '+56',
        telefono: {
            regex: /^9[0-9]{8}$/,
            digitos: 9,
            placeholder: '9 1234 5678',
            ejemplo: '912345678',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            { codigo: 'RUT', nombre: 'RUT (Rol Único Tributario)', digitos: null, placeholder: '12345678-9' },
        ],
        regimenes: [
            { codigo: '14A', nombre: 'Régimen General Semi-Integrado (14A)' },
            { codigo: '14D3', nombre: 'Régimen Pro-Pyme General (14D N°3)' },
            { codigo: '14D8', nombre: 'Régimen Pro-Pyme Transparente (14D N°8)' },
            { codigo: '14BIS', nombre: 'Régimen 14 Bis (simplificado)' },
        ],
        bancos: [
            'Banco de Chile',
            'BancoEstado',
            'Santander Chile',
            'BCI (Banco de Crédito e Inversiones)',
            'Scotiabank Chile',
            'Itaú Chile',
            'Banco Falabella Chile',
            'Banco Ripley Chile',
            'Banco BICE',
            'Banco Security',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'vista', nombre: 'Cuenta Vista / Cuenta RUT' },
            { codigo: 'ahorro', nombre: 'Cuenta de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Patente Municipal',
            tiene_vencimiento: true,
        },
    },

    MX: {
        nombre: 'México',
        prefijo: '+52',
        telefono: {
            regex: /^[1-9][0-9]{9}$/,
            digitos: 10,
            placeholder: '55 1234 5678',
            ejemplo: '5512345678',
            mensajeError: 'Celular invalido',
        },
        moneda: 'MXN',
        documentos_fiscales: [
            {
                codigo: 'RFC',
                nombre: 'RFC (Registro Federal de Contribuyentes)',
                digitos: 13,
                placeholder: 'XAXX010101000',
            },
            { codigo: 'CURP', nombre: 'CURP', digitos: 18, placeholder: 'GARC850101HDFRRL09' },
        ],
        regimenes: [
            { codigo: 'RIF', nombre: 'Régimen de Incorporación Fiscal' },
            { codigo: 'RESICO', nombre: 'Régimen Simplificado de Confianza (RESICO)' },
            { codigo: 'PF_AE', nombre: 'Persona Física con Actividad Empresarial' },
            { codigo: 'PM_G', nombre: 'Persona Moral - Régimen General' },
        ],
        bancos: [
            'BBVA México',
            'Banorte',
            'Santander México',
            'Citibanamex',
            'HSBC México',
            'Scotiabank México',
            'Banco Azteca',
            'Banco del Bienestar',
            'BanCoppel',
            'Inbursa',
        ],
        tipos_cuenta: [
            { codigo: 'cheques', nombre: 'Cuenta de Cheques' },
            { codigo: 'debito', nombre: 'Cuenta de Débito' },
            { codigo: 'ahorro', nombre: 'Cuenta de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'CLABE Interbancaria',
            digitos: 18,
            placeholder: '012345678901234567',
        },
        autorizacion: {
            nombre: 'Licencia de Funcionamiento Municipal',
            tiene_vencimiento: true,
        },
    },

    AR: {
        nombre: 'Argentina',
        moneda: 'ARS',
        prefijo: '+54',
        telefono: {
            regex: /^[1-9][0-9]{9,10}$/,
            digitos: 10,
            placeholder: '11 1234 5678',
            ejemplo: '1112345678',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'CUIT',
                nombre: 'CUIT (Clave Única de Identificación Tributaria)',
                digitos: 11,
                placeholder: '20123456789',
            },
            {
                codigo: 'CUIL',
                nombre: 'CUIL (Código Único de Identificación Laboral)',
                digitos: 11,
                placeholder: '20123456789',
            },
            { codigo: 'DNI', nombre: 'DNI (Documento Nacional de Identidad)', digitos: 8, placeholder: '12345678' },
        ],
        regimenes: [
            { codigo: 'MONO', nombre: 'Monotributo' },
            { codigo: 'RI', nombre: 'Responsable Inscripto' },
            { codigo: 'EX', nombre: 'Exento' },
            { codigo: 'CF', nombre: 'Consumidor Final' },
        ],
        bancos: [
            'Banco Nación Argentina',
            'Banco Provincia',
            'Banco Galicia',
            'Banco Santander Argentina',
            'BBVA Argentina',
            'Banco Macro',
            'Banco HSBC Argentina',
            'Banco Patagonia',
            'Banco Ciudad',
            'Mercado Pago',
        ],
        tipos_cuenta: [
            { codigo: 'ca_pesos', nombre: 'Caja de Ahorro en Pesos' },
            { codigo: 'cc_pesos', nombre: 'Cuenta Corriente en Pesos' },
            { codigo: 'ca_usd', nombre: 'Caja de Ahorro en Dólares' },
        ],
        cuenta_interbancaria: {
            nombre: 'CBU (Clave Bancaria Uniforme)',
            digitos: 22,
            placeholder: '0110012340001234567890',
        },
        autorizacion: {
            nombre: 'Habilitación Comercial Municipal',
            tiene_vencimiento: true,
        },
    },

    UY: {
        nombre: 'Uruguay',
        moneda: 'UYU',
        prefijo: '+598',
        telefono: {
            regex: /^9[0-9]{7}$/,
            digitos: 8,
            placeholder: '9123 4567',
            ejemplo: '91234567',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            { codigo: 'RUT', nombre: 'RUT (Registro Único Tributario)', digitos: 12, placeholder: '211234560019' },
            { codigo: 'CI', nombre: 'Cédula de Identidad', digitos: 8, placeholder: '12345678' },
        ],
        regimenes: [
            { codigo: 'CEDE', nombre: 'CEDE (Contribuyente de Derecho)' },
            { codigo: 'LIT', nombre: 'Literal E' },
            { codigo: 'MONO', nombre: 'Monotributo' },
            { codigo: 'ME', nombre: 'Pequeña Empresa' },
        ],
        bancos: [
            'BROU (Banco República)',
            'Santander Uruguay',
            'Itaú Uruguay',
            'Scotiabank Uruguay',
            'BBVA Uruguay',
            'Banco Heritage',
            'HSBC Uruguay',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorro', nombre: 'Caja de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Habilitación de Bomberos / Permiso BPS',
            tiene_vencimiento: true,
        },
    },

    PY: {
        nombre: 'Paraguay',
        moneda: 'PYG',
        prefijo: '+595',
        telefono: {
            regex: /^9[0-9]{8}$/,
            digitos: 9,
            placeholder: '981 123 456',
            ejemplo: '981123456',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'RUC',
                nombre: 'RUC (Registro Único de Contribuyentes)',
                digitos: null,
                placeholder: '80012345-6',
            },
            { codigo: 'CI', nombre: 'Cédula de Identidad', digitos: null, placeholder: '1234567' },
        ],
        regimenes: [
            { codigo: 'SIMPLE', nombre: 'Régimen Simplificado' },
            { codigo: 'RESIMPLE', nombre: 'RESIMPLE (Micro y Pequeña Empresa)' },
            { codigo: 'IRE_G', nombre: 'IRE General' },
            { codigo: 'IRE_S', nombre: 'IRE Simplificado' },
        ],
        bancos: [
            'Banco Nacional de Fomento',
            'Banco Continental',
            'Itaú Paraguay',
            'Banco Regional',
            'Sudameris',
            'Banco Atlas',
            'Visión Banco',
            'Banco Familiar',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorro', nombre: 'Caja de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Patente Comercial Municipal',
            tiene_vencimiento: true,
        },
    },

    SV: {
        nombre: 'El Salvador',
        moneda: 'USD',
        prefijo: '+503',
        telefono: {
            // Móviles salvadoreños: 8 dígitos, marcación directa (sin código de
            // área), comienzan en 6 o 7.
            regex: /^[67][0-9]{7}$/,
            digitos: 8,
            placeholder: '7123 4567',
            ejemplo: '71234567',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'NIT',
                nombre: 'NIT (Número de Identificación Tributaria)',
                digitos: 14,
                placeholder: '06141234567890',
            },
            { codigo: 'DUI', nombre: 'DUI (Documento Único de Identidad)', digitos: 9, placeholder: '012345678' },
        ],
        regimenes: [
            { codigo: 'GC', nombre: 'Gran Contribuyente' },
            { codigo: 'MC', nombre: 'Mediano Contribuyente' },
            { codigo: 'OC', nombre: 'Otros Contribuyentes' },
        ],
        bancos: [
            'Banco Agrícola',
            'Banco Cuscatlán',
            'Banco Davivienda El Salvador',
            'Banco de América Central (BAC)',
            'Banco Hipotecario',
            'Banco Promerica',
            'Banco Atlántida El Salvador',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorro', nombre: 'Cuenta de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Licencia / Matrícula de Empresa (CNR)',
            tiene_vencimiento: true,
        },
    },

    GT: {
        nombre: 'Guatemala',
        moneda: 'GTQ',
        prefijo: '+502',
        telefono: {
            // Móviles guatemaltecos: 8 dígitos, marcación directa (sin código de
            // área), comienzan en 3, 4 o 5.
            regex: /^[345][0-9]{7}$/,
            digitos: 8,
            placeholder: '5123 4567',
            ejemplo: '51234567',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'NIT',
                nombre: 'NIT (Número de Identificación Tributaria)',
                digitos: null,
                placeholder: '1234567-8',
            },
            {
                codigo: 'DPI',
                nombre: 'DPI (Documento Personal de Identificación)',
                digitos: 13,
                placeholder: '1234567890101',
            },
        ],
        regimenes: [
            { codigo: 'GEN', nombre: 'Régimen sobre Utilidades de Actividades Lucrativas' },
            { codigo: 'OPC', nombre: 'Régimen Opcional Simplificado sobre Ingresos' },
            { codigo: 'PEQ', nombre: 'Régimen de Pequeño Contribuyente' },
        ],
        bancos: [
            'Banco Industrial',
            'Banco G&T Continental',
            'Banrural',
            'Banco de América Central (BAC)',
            'Banco Promerica Guatemala',
            'Banco Agromercantil (BAM)',
            'Banco Internacional',
        ],
        tipos_cuenta: [
            { codigo: 'monetaria', nombre: 'Cuenta Monetaria' },
            { codigo: 'ahorro', nombre: 'Cuenta de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Patente de Comercio',
            tiene_vencimiento: true,
        },
    },

    CR: {
        nombre: 'Costa Rica',
        moneda: 'CRC',
        prefijo: '+506',
        telefono: {
            // Móviles costarricenses: 8 dígitos, marcación directa (sin código de
            // área), comienzan en 6, 7 u 8.
            regex: /^[678][0-9]{7}$/,
            digitos: 8,
            placeholder: '8312 3456',
            ejemplo: '83123456',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'CJ',
                nombre: 'Cédula Jurídica',
                digitos: 10,
                placeholder: '3101123456',
            },
            { codigo: 'CF', nombre: 'Cédula Física', digitos: 9, placeholder: '112340567' },
            { codigo: 'DIMEX', nombre: 'DIMEX (extranjeros)', digitos: null, placeholder: '' },
        ],
        regimenes: [
            { codigo: 'TRAD', nombre: 'Régimen Tradicional' },
            { codigo: 'SIMPL', nombre: 'Régimen Simplificado' },
        ],
        bancos: [
            'Banco Nacional de Costa Rica',
            'Banco de Costa Rica (BCR)',
            'BAC Credomatic',
            'Banco Popular',
            'Scotiabank Costa Rica',
            'Davivienda Costa Rica',
            'Banco Promerica',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorro', nombre: 'Cuenta de Ahorro' },
        ],
        cuenta_interbancaria: {
            nombre: 'IBAN / Cuenta Cliente',
            digitos: null,
            placeholder: 'CR05015202001026284066',
        },
        autorizacion: {
            nombre: 'Patente Municipal',
            tiene_vencimiento: true,
        },
    },

    EC: {
        nombre: 'Ecuador',
        moneda: 'USD',
        prefijo: '+593',
        telefono: {
            regex: /^0?9[0-9]{8}$/,
            digitos: 10,
            placeholder: '09 9123 4567',
            ejemplo: '0991234567',
            mensajeError: 'Celular invalido',
        },
        documentos_fiscales: [
            {
                codigo: 'RUC',
                nombre: 'RUC (Registro Único de Contribuyentes)',
                digitos: 13,
                placeholder: '1712345678001',
            },
            { codigo: 'CI', nombre: 'Cédula de Identidad', digitos: 10, placeholder: '1712345678' },
        ],
        regimenes: [
            { codigo: 'RIMPE_N', nombre: 'RIMPE - Negocio Popular' },
            { codigo: 'RIMPE_E', nombre: 'RIMPE - Emprendedor' },
            { codigo: 'GENERAL', nombre: 'Régimen General' },
        ],
        bancos: [
            'Banco Pichincha',
            'Banco del Pacífico',
            'Banco de Guayaquil',
            'Produbanco',
            'Banco Internacional',
            'Banco Bolivariano',
        ],
        tipos_cuenta: [
            { codigo: 'corriente', nombre: 'Cuenta Corriente' },
            { codigo: 'ahorros', nombre: 'Cuenta de Ahorros' },
        ],
        cuenta_interbancaria: {
            nombre: 'Número de Cuenta',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'LUAE (Licencia Única de Actividades Económicas)',
            tiene_vencimiento: true,
        },
    },

    US: {
        nombre: 'Estados Unidos',
        moneda: 'USD',
        prefijo: '+1',
        telefono: {
            regex: /^[2-9]\d{2}[2-9]\d{6}$/,
            digitos: 10,
            placeholder: '212 555 1234',
            ejemplo: '2125551234',
            mensajeError: 'Phone invalid (10 digits, area code first)',
        },
        documentos_fiscales: [
            { codigo: 'EIN', nombre: 'Employer Identification Number', digitos: 9, placeholder: '12-3456789' },
            { codigo: 'SSN', nombre: 'Social Security Number', digitos: 9, placeholder: '123-45-6789' },
            {
                codigo: 'ITIN',
                nombre: 'Individual Taxpayer Identification Number',
                digitos: 9,
                placeholder: '9XX-XX-XXXX',
            },
        ],
        regimenes: [
            { codigo: 'C_CORP', nombre: 'C Corporation' },
            { codigo: 'S_CORP', nombre: 'S Corporation' },
            { codigo: 'LLC', nombre: 'Limited Liability Company' },
            { codigo: 'SOLE_PROP', nombre: 'Sole Proprietorship' },
        ],
        bancos: [
            'Chase',
            'Bank of America',
            'Wells Fargo',
            'Citibank',
            'U.S. Bank',
            'PNC Bank',
            'Capital One',
            'TD Bank',
        ],
        tipos_cuenta: [
            { codigo: 'checking', nombre: 'Checking Account' },
            { codigo: 'savings', nombre: 'Savings Account' },
        ],
        cuenta_interbancaria: {
            nombre: 'Routing Number + Account Number',
            digitos: null,
            placeholder: '',
        },
        autorizacion: {
            nombre: 'Business License',
            tiene_vencimiento: true,
        },
    },
};

/** Códigos de país disponibles */
const PAISES_DISPONIBLES = Object.keys(DATOS_FISCALES_POR_PAIS).map(codigo => ({
    codigo,
    nombre: DATOS_FISCALES_POR_PAIS[codigo].nombre,
    moneda: DATOS_FISCALES_POR_PAIS[codigo].moneda,
}));

/** Obtener preset de un país. Retorna null si no existe (usar opción "Otro"). */
function getPresetPais(codigoPais) {
    return DATOS_FISCALES_POR_PAIS[codigoPais] || null;
}

/**
 * Obtener prefijo telefónico internacional ('+51', '+55', etc.) por código
 * ISO-2 de país. Si el país no está en el catálogo y el caller no pasa
 * fallback explícito, devuelve `null` (caller decide qué hacer).
 *
 * Cambio multi-brand 2026-05-08: el fallback antes era '+51' hardcoded peruano,
 * lo cual daba respuesta peruana a nodos de otras brands silenciosamente.
 * Ahora el caller debe pasar fallback explícito o aceptar null.
 *
 * @param {string} codigoPais - 'PE', 'BR', 'CO', ...
 * @param {string} [fallback] - prefijo a usar si no hay preset. Sin fallback → null.
 */
function getPrefijoPais(codigoPais, fallback) {
    var preset = DATOS_FISCALES_POR_PAIS[codigoPais];
    if (preset && preset.prefijo) return preset.prefijo;
    return typeof fallback === 'string' ? fallback : null;
}

/**
 * Mapa moneda → codigo pais (para resolver pais desde config del nodo).
 *
 * ⚠️ NO ES BIYECTIVA: USD lo usan EC y US. La última entrada del loop gana.
 * Si el nodo usa una moneda compartida (USD), preferí leer `nodo.config.pais`
 * directo en vez de derivarlo de la moneda. Este mapa solo sirve para casos
 * con monedas únicas (PEN→PE, BRL→BR, etc.).
 */
var MONEDA_A_PAIS = {};
for (var _k in DATOS_FISCALES_POR_PAIS) {
    MONEDA_A_PAIS[DATOS_FISCALES_POR_PAIS[_k].moneda] = _k;
}

/**
 * Valida telefono segun reglas del pais.
 * @param {string} telefono - Solo digitos (sin espacios, guiones, etc.)
 * @param {string} codigoPais - 'BR', 'PE', 'CO', etc.
 * @returns {{ valido: boolean, mensajeError?: string }}
 */
function validarTelefono(telefono, codigoPais) {
    var preset = DATOS_FISCALES_POR_PAIS[codigoPais];
    if (!preset || !preset.telefono) return { valido: true };
    if (preset.telefono.regex.test(telefono)) return { valido: true };
    return { valido: false, mensajeError: preset.telefono.mensajeError };
}

// ── Triple compatibilidad: ESM / CJS / IIFE ──
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        DATOS_FISCALES_POR_PAIS,
        PAISES_DISPONIBLES,
        getPresetPais,
        getPrefijoPais,
        validarTelefono,
        MONEDA_A_PAIS,
    };
}
if (typeof window !== 'undefined') {
    window.DatosFiscalesPorPais = {
        DATOS_FISCALES_POR_PAIS,
        PAISES_DISPONIBLES,
        getPresetPais,
        getPrefijoPais,
        validarTelefono,
        MONEDA_A_PAIS,
    };
}
export { DATOS_FISCALES_POR_PAIS, PAISES_DISPONIBLES, getPresetPais, getPrefijoPais, validarTelefono, MONEDA_A_PAIS };
